import { memo, useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { cardClass } from './dashboardTokens';
import { DashboardSectionHeader } from './DashboardSectionHeader';

export type PassFailDonutSegment = 'within' | 'out';

interface PassFailDonutChartProps {
  data: { name: string; value: number; color: string }[];
  passRatePct: number | null;
  onSegmentClick?: (segment: PassFailDonutSegment) => void;
}

function segmentFromName(name: string): PassFailDonutSegment | null {
  const n = name.toLowerCase();
  if (n.includes('within')) return 'within';
  if (n.includes('out')) return 'out';
  return null;
}

const DonutCenter = ({ passRatePct }: { passRatePct: number | null }) => (
  <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
    <span className="font-heading text-3xl font-bold text-blueprint-blue">
      {passRatePct != null ? `${passRatePct.toFixed(1)}%` : '—'}
    </span>
    <span className="text-xs text-heritage-grey">Compliance rate</span>
  </div>
);

export const PassFailDonutChart = memo(({
  data,
  passRatePct,
  onSegmentClick,
}: PassFailDonutChartProps) => {
  const total = useMemo(() => data.reduce((s, d) => s + d.value, 0), [data]);
  const isEmpty = total === 0;

  return (
    <div className={cardClass}>
      <DashboardSectionHeader
        title="Within limit vs out of limit"
        subtitle="Weld WPS limit distribution. Click a segment to inspect welds."
        helpKey="within_vs_out_limit_chart"
      />
      {isEmpty ? (
        <div className="flex h-64 flex-col items-center justify-center gap-1 px-6 text-center text-sm text-heritage-grey">
          <span>No weld outcomes yet</span>
          <span className="text-xs">
            Welds need a WPS status (TRUE/FALSE) from production weld-status to count as within or out of limit.
          </span>
        </div>
      ) : (
        <div className="relative mt-2 h-72">
          <DonutCenter passRatePct={passRatePct} />
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius="58%"
                outerRadius="78%"
                paddingAngle={2}
                animationDuration={600}
                cursor={onSegmentClick ? 'pointer' : 'default'}
                onClick={(entry) => {
                  if (!onSegmentClick) return;
                  const name = String((entry as { name?: string })?.name ?? '');
                  const segment = segmentFromName(name);
                  if (segment) onSegmentClick(segment);
                }}
              >
                {data.map((entry) => (
                  <Cell
                    key={entry.name}
                    fill={entry.color}
                    stroke="transparent"
                    cursor={onSegmentClick ? 'pointer' : 'default'}
                    onClick={() => {
                      if (!onSegmentClick) return;
                      const segment = segmentFromName(entry.name);
                      if (segment) onSegmentClick(segment);
                    }}
                  />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number, name: string) => [
                  `${value} (${total > 0 ? ((value / total) * 100).toFixed(1) : 0}%)`,
                  name,
                ]}
              />
              <Legend verticalAlign="bottom" height={36} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
});

PassFailDonutChart.displayName = 'PassFailDonutChart';
